import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MobileAlert } from "./components/MobileAlert";
import AppLayout from "@/components/AppLayout";
import SessionManager from "@/components/SessionManager";
import HubSpotScript from './components/HubSpotScript';
import { Button } from "@/components/ui/button";

// Lazy load all page components to reduce initial bundle size
// Core pages (use eager loading for critical pages)
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';

// Lazy loaded pages (loaded only when needed)
const Index = lazy(() => import('./pages/Index'));
// Simple import with basic error handling in the suspense fallback
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Intro = lazy(() => import('./pages/Intro'));
const FocusFlowIntro = lazy(() => import('./pages/FocusFlowIntro'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const OptimizedStageOne = lazy(() => import('./pages/OptimizedStageOne'));
const StageTwo = lazy(() => import('./pages/StageTwo'));
const StageThree = lazy(() => import('./pages/StageThree'));
const StageFour = lazy(() => import('./pages/StageFour'));
const Workspace = lazy(() => import('./pages/Workspace'));
const Tasks = lazy(() => import('./pages/Tasks'));
const TasksAdmin = lazy(() => import('./pages/TasksAdmin'));
const SalesOpsSupportRequest = lazy(() => import('./pages/SalesOpsSupportRequest'));
const WorkHistory_RevenueHub = lazy(() => import('./pages/WorkHistory_RevenueHub'));
const StatusPrioridades = lazy(() => import('./pages/StatusPrioridades'));
const Profile = lazy(() => import('./pages/Profile'));
const Support = lazy(() => import('./pages/Support'));
const Admin = lazy(() => import('./pages/Admin'));
const Booking = lazy(() => import('./pages/Booking'));
const BookingHelp = lazy(() => import('./pages/BookingHelp'));

// Lazy-load more pages
const OnboardingBasico = lazy(() => import('./pages/OnboardingBasico'));
const Certificaciones = lazy(() => import('./pages/Certificaciones'));
const Tutoriales = lazy(() => import('./pages/Tutoriales'));
const TicketBoard = lazy(() => import('./pages/TicketBoard'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Contract = lazy(() => import('./pages/Contract'));
const TimelineConsultoria = lazy(() => import('./pages/TimelineConsultoria'));
const NotificationsCenter = lazy(() => import('./pages/NotificationsCenter'));

// Loading screen for authentication and session management
const LoadingScreen = () => {
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const [showLogoutOption, setShowLogoutOption] = React.useState(false);
  
  const handleForceLogout = () => {
    setIsLoggingOut(true);
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/auth';
  };
  
  // Show logout option after a delay if loading takes too long
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowLogoutOption(true);
    }, 8000); // Show after 8 seconds
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8 space-y-8 bg-white rounded-lg shadow-lg">
        <div className="flex justify-center">
          {/* Logo or brand element could go here */}
          <svg 
            className="w-16 h-16 text-mainBlue animate-pulse" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
            />
          </svg>
        </div>
        
        <h2 className="mt-2 text-center text-xl font-medium text-gray-900">
          Cargando RevenueHub
        </h2>
        
        <div className="relative">
          <div className="overflow-hidden h-2 mb-4 mt-6 text-xs flex rounded bg-gray-200">
            <div className="animate-loadingBar w-full h-full bg-mainBlue"></div>
          </div>
        </div>
        
        {showLogoutOption && (
          <div className="text-center mt-6 space-y-4">
            <p className="text-sm text-gray-600">
              La carga está tardando más de lo habitual. Si continúa así, puede intentar reiniciar la sesión.
            </p>
            <button 
              onClick={handleForceLogout}
              disabled={isLoggingOut}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-mainBlue bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mainBlue transition-colors"
            >
              {isLoggingOut ? "Reiniciando sesión..." : "Reiniciar sesión"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple ErrorBoundary component to catch render errors
class SimpleErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="p-6 text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error al cargar la página</h2>
          <p className="mb-4">Ha ocurrido un error al cargar esta página. Por favor, recarga la aplicación.</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Recargar la aplicación
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper for lazy-loaded pages to simplify route definitions and add error handling
const LazyPage = ({ children }: { children: React.ReactNode }) => {
  return (
    <SimpleErrorBoundary>
      <Suspense fallback={<div className="p-6 text-center">Cargando página...</div>}>
        {children}
      </Suspense>
    </SimpleErrorBoundary>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading, logout } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!session) {
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  return <SessionManager>{children}</SessionManager>;
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading, isAdmin, logout } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SessionManager>{children}</SessionManager>;
};

const HubSpotSupportRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading, isAdmin, hasHubspotSupport, logout } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin && !hasHubspotSupport) {
    return <Navigate to="/dashboard" replace />;
  }

  return <SessionManager>{children}</SessionManager>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = (location.state as { from?: string })?.from || "/dashboard";
  
  React.useEffect(() => {
    if (!loading && session) {
      console.log('PublicRoute: Authenticated, redirecting to', from);
      navigate(from, { replace: true });
    }
  }, [loading, session, navigate, from]);
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!session) {
    return <>{children}</>;
  }
  
  return <div>Redirigiendo...</div>;
};

const App = () => {
  const { session } = useAuth();
  const location = useLocation();

  const publicRoutes = [
    '/welcome',
    '/contract',
    '/auth',
    '/timeline-consultoria'
  ];

  const isPublicRoute = publicRoutes.some(route => location.pathname === route);

  return (
    <>
      <HubSpotScript />
      <Routes>

        <Route path="/" element={<Navigate to="/index" replace />} />
        <Route
          path="/index"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Index />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/intro"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Intro />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/focusflow-intro"
          element={
            <ProtectedRoute>
              <AppLayout>
                <FocusFlowIntro />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/terms-and-conditions"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TermsAndConditions />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stage-one"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LazyPage>
                  <OptimizedStageOne />
                </LazyPage>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stage-two"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StageTwo />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stage-three"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StageThree />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stage-four"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StageFour />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Workspace />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/notifications-center"
          element={
            <ProtectedRoute>
              <AppLayout>
                <NotificationsCenter />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/tasks"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Tasks />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workspace/tasks-admin"
          element={
            <AdminRoute>
              <AppLayout>
                <TasksAdmin />
              </AppLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/workspace/salesops/new"
          element={
            <HubSpotSupportRoute>
              <AppLayout>
                <SalesOpsSupportRequest />
              </AppLayout>
            </HubSpotSupportRoute>
          }
        />
        <Route
          path="/workspace/salesops/history"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WorkHistory_RevenueHub />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/status-prioridades"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StatusPrioridades />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Profile />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/support"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Support />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AppLayout>
                <Admin />
              </AppLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/auth"
          element={
            <PublicRoute>
              <Auth />
            </PublicRoute>
          }
        />
        <Route
          path="/booking"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Booking />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/booking-help"
          element={
            <ProtectedRoute>
              <AppLayout>
                <BookingHelp />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding-basico"
          element={
            <ProtectedRoute>
              <AppLayout>
                <OnboardingBasico />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificaciones"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Certificaciones />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutoriales"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Tutoriales />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TicketBoard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/welcome"
          element={<Welcome />}
        />
        <Route
          path="/contract"
          element={<Contract />}
        />
        <Route
          path="/timeline-consultoria"
          element={<TimelineConsultoria />}
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isPublicRoute && session && <MobileAlert />}
    </>
  );
};

export default App;
